"use client";

import { useState } from "react";
import { useWaitForTransactionReceipt } from "wagmi";
import { useTransactionToast } from "@/hooks/use-transaction-toast";

type Hash = `0x${string}`;

interface UseWriteWithToastOptions {
  title: string;
  onSuccess?: () => Promise<void> | void;
  onError?: (error: unknown) => void;
}

export function useWriteWithToast({ title, onSuccess, onError }: UseWriteWithToastOptions) {
  const toast = useTransactionToast({ title, onSuccess, onError });
  const [hash, setHash] = useState<Hash | undefined>(undefined);

  const { isLoading } = useWaitForTransactionReceipt({
    hash,
    query: { enabled: !!hash },
  });

  async function run(write: () => Promise<Hash | undefined>): Promise<Hash | undefined> {
    toast.initiate();
    try {
      const sent = await write();
      if (sent) {
        setHash(sent);
        toast.trackTransaction(sent);
      }
      return sent;
    } catch (error) {
      toast.onException(error);
      return undefined;
    }
  }

  async function runMany(writes: Array<() => Promise<Hash | undefined>>): Promise<Hash[]> {
    const total = writes.length;
    const hashes: Hash[] = [];
    if (total === 0) return hashes;
    toast.initiate(1, total);
    for (let index = 0; index < writes.length; index++) {
      try {
        const sent = await writes[index]();
        if (sent) {
          hashes.push(sent);
          setHash(sent);
          toast.trackTransaction(sent, index + 1, total);
        }
      } catch (error) {
        toast.onException(error);
        break;
      }
    }
    return hashes;
  }

  return { run, runMany, isLoading, hash };
}
